import {
  useCallback,
  useState,
  useMemo,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import { useToast } from '@chakra-ui/react';
import { EntityInstance } from '../types';
import {
  useAddExcludedEntitiesToAlertMetadata,
  useRemoveExcludedEntitiesFromAlertMetadata,
  QUERY_ALERT_METADATA_EXCLUDED_ENTITIES_PAGINATED,
} from '../../detections/queries';
import { i18n } from '../../../shared/localization';
import { validateSuppressionContext } from '../utils/validateSuppressionContext';

// Enum for suppression actions
export enum SuppressionAction {
  SUPPRESS = 'suppress',
  UNSUPPRESS = 'unsuppress',
}

interface UseEntitySuppressionParams {
  selectedEntity?: EntityInstance;
  setSelectedEntity: Dispatch<SetStateAction<EntityInstance | undefined>>;
}

interface MutationResult {
  success: boolean;
  error?: string;
}

/**
 * Custom hook for entity suppression functionality.
 * Handles suppression mutations and validation state.
 * @param selectedEntity - The currently selected entity
 * @param setSelectedEntity - Setter to update the selected entity state
 *
 * @returns Suppression state (loading, enabled, error) and updateSuppression mutation function
 */
export const useEntitySuppression = ({
  selectedEntity,
  setSelectedEntity,
}: UseEntitySuppressionParams) => {
  // Track mutation loading and error states
  const [pendingUpdate, setPendingUpdate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  // GraphQL mutation hooks
  const [addExcludedEntitiesMutation] = useAddExcludedEntitiesToAlertMetadata(
    {},
  );
  const [removeExcludedEntitiesMutation] =
    useRemoveExcludedEntitiesFromAlertMetadata({});

  // Reset pendingUpdate and error state when selectedEntity changes
  useEffect(() => {
    setPendingUpdate(false);
    setError(null);
  }, [selectedEntity?.entityId]);

  // Calculate validation state (enabled, loading)
  const validationState = useMemo(() => {
    // Check if we're still waiting for initial data
    // entityIsSuppressed undefined means query hasn't completed yet
    const initialDataLoading =
      !selectedEntity?.entityCategory ||
      !selectedEntity?.detectionData ||
      selectedEntity?.entityIsSuppressed === undefined;
    const loading = pendingUpdate || initialDataLoading;

    // 1. Check entity category
    const isSourceEntity = selectedEntity?.entityCategory === 'source';

    // 2. Validate required fields
    const validation = validateSuppressionContext(
      selectedEntity?.detectionData,
    );
    const hasRequiredFields = validation.isValid;

    // 3. Check single detection requirement
    // If requireSingleDetectionForSuppression is true, only allow if exactly 1 detection
    // Prefer detection_count (total count) over detectionDataList.length (paginated results)
    const actualDetectionCount =
      selectedEntity?.detectionData?.detection_count ??
      selectedEntity?.detectionDataList?.length;
    const passesConditionalCheck =
      !selectedEntity?.requireSingleDetectionForSuppression ||
      actualDetectionCount === 1;

    // 4. Calculate if entity can be managed (suppressed OR unsuppressed)
    // This determines button enabled/disabled state
    // Current suppression status determines the action (suppress vs unsuppress)
    const enabled =
      isSourceEntity && hasRequiredFields && passesConditionalCheck && !loading;

    return {
      enabled,
      loading,
    };
  }, [
    selectedEntity?.entityCategory,
    selectedEntity?.detectionData,
    selectedEntity?.entityIsSuppressed,
    selectedEntity?.requireSingleDetectionForSuppression,
    selectedEntity?.detectionDataList?.length,
    pendingUpdate,
  ]);

  /**
   * Suppress (exclude) an entity from generating future alerts
   */
  const suppressEntity = useCallback(async (): Promise<MutationResult> => {
    if (!selectedEntity) {
      return { success: false, error: 'No active entity' };
    }

    const { entityName, entityType, detectionData, tenantInfo } =
      selectedEntity;
    const { content_id, alert_name, alert_type } = detectionData || {};

    // Validate required fields
    if (!content_id || !alert_name || !alert_type || !tenantInfo?.tenant_id) {
      const errorMsg = 'Missing required alert context';
      toast({
        status: 'error',
        title: 'Cannot Suppress Entity',
        description: errorMsg,
        isClosable: true,
        position: 'bottom-right',
      });
      return { success: false, error: errorMsg };
    }

    // Set loading state
    setPendingUpdate(true);
    setError(null);

    try {
      await addExcludedEntitiesMutation({
        variables: {
          items: [
            {
              entity_name: entityName,
              entity_type: entityType,
              tenant: tenantInfo.tenant_id,
              content_id,
              alert_name,
              alert_type,
            },
          ],
        },
        refetchQueries: [
          {
            query: QUERY_ALERT_METADATA_EXCLUDED_ENTITIES_PAGINATED,
            variables: selectedEntity.excludedEntitiesVariables,
          },
        ],
        awaitRefetchQueries: true,
      });

      // Update entity state
      setSelectedEntity((prev) =>
        prev ? { ...prev, entityIsSuppressed: true } : prev,
      );

      setPendingUpdate(false);

      // Show success toast
      toast({
        status: 'success',
        title: i18n.pages.detections.toasts.success,
        description: i18n.formatString(
          i18n.pages.detections.toasts.addEntitySuccessBody,
          entityName || '',
        ),
        isClosable: true,
        position: 'bottom-right',
      });

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';

      setPendingUpdate(false);
      setError(errorMessage);

      toast({
        status: 'error',
        title: i18n.pages.detections.toasts.error,
        description: i18n.formatString(
          i18n.pages.detections.toasts.addEntityErrorBody,
          entityName || '',
        ),
        isClosable: true,
        position: 'bottom-right',
      });

      return { success: false, error: errorMessage };
    }
  }, [selectedEntity, addExcludedEntitiesMutation, setSelectedEntity, toast]);

  /**
   * Unsuppress (restore) an entity to generate alerts again
   */
  const unsuppressEntity = useCallback(async (): Promise<MutationResult> => {
    if (!selectedEntity) {
      return { success: false, error: 'No active entity' };
    }

    const { entityName, entityType, detectionData, tenantInfo } =
      selectedEntity;
    const { content_id, alert_name, alert_type } = detectionData || {};

    // Validate required fields
    if (!content_id || !alert_name || !alert_type || !tenantInfo?.tenant_id) {
      const errorMsg = 'Missing required alert context';
      toast({
        status: 'error',
        title: 'Cannot Unsuppress Entity',
        description: errorMsg,
        isClosable: true,
        position: 'bottom-right',
      });
      return { success: false, error: errorMsg };
    }

    // Set loading state
    setPendingUpdate(true);
    setError(null);

    try {
      await removeExcludedEntitiesMutation({
        variables: {
          items: [
            {
              entity_name: entityName,
              entity_type: entityType,
              tenant: tenantInfo.tenant_id,
              content_id,
              alert_name,
              alert_type,
            },
          ],
        },
        refetchQueries: [
          {
            query: QUERY_ALERT_METADATA_EXCLUDED_ENTITIES_PAGINATED,
            variables: selectedEntity.excludedEntitiesVariables,
          },
        ],
        awaitRefetchQueries: true,
      });

      // Update entity state
      setSelectedEntity((prev) =>
        prev ? { ...prev, entityIsSuppressed: false } : prev,
      );

      setPendingUpdate(false);

      // Show success toast
      toast({
        status: 'success',
        title: i18n.pages.detections.toasts.success,
        description: i18n.formatString(
          i18n.pages.detections.toasts.removeEntitySuccessBody,
          entityName || '',
        ),
        isClosable: true,
        position: 'bottom-right',
      });

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';

      setPendingUpdate(false);
      setError(errorMessage);

      toast({
        status: 'error',
        title: i18n.pages.detections.toasts.error,
        description: i18n.formatString(
          i18n.pages.detections.toasts.removeEntityErrorBody,
          entityName || '',
        ),
        isClosable: true,
        position: 'bottom-right',
      });

      return { success: false, error: errorMessage };
    }
  }, [selectedEntity, removeExcludedEntitiesMutation, toast]);

  // Wrapper function that takes an action parameter
  const updateSuppression = useCallback(
    async (action: SuppressionAction) => {
      return action === SuppressionAction.SUPPRESS
        ? await suppressEntity()
        : await unsuppressEntity();
    },
    [suppressEntity, unsuppressEntity],
  );

  return {
    pendingUpdate,
    error,
    enabled: validationState.enabled,
    loading: validationState.loading,
    updateSuppression,
  };
};
